// Análise de Clientes do Memor.IA — página do adm.
// Todas as reuniões analisadas com cliente, agrupadas por cliente, com nota média, tendência e
// setores; filtros por setor (produto da reunião), tipo, período e busca. Clicar numa reunião
// abre a análise completa (ReuniaoAnaliseModal). Gestão/1:1 não entra (reunião interna).
// Backend: core/views_memora_clientes.py
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import ReuniaoAnaliseModal from '../components/ReuniaoAnaliseModal';
import MemoraClienteDashboard from '../components/MemoraClienteDashboard';
import { labelTipoReuniao } from '../utils/competencias';

const API = process.env.REACT_APP_API_BASE;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } });
const fmtNota = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const fmtData = (d) => {
  if (!d) return '-';
  const p = String(d).slice(0, 10).split('-');
  return p.length < 3 ? d : `${p[2]}/${p[1]}/${p[0]}`;
};
const badgeNota = (n) => {
  if (n === null || n === undefined) return 'bg-secondary';
  if (n >= 8) return 'bg-success';
  if (n >= 6) return 'bg-primary';
  if (n >= 4) return 'bg-warning text-dark';
  return 'bg-danger';
};
const media = (lista) => {
  const ns = lista.filter((x) => x !== null && x !== undefined);
  return ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : null;
};
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const SEM_CLIENTE = 'Cliente não identificado';

const TIPOS = [
  { key: 'comercial', label: 'Comercial / Vendas' },
  { key: 'servico', label: 'Serviço / Consultivo' },
  { key: 'cs', label: 'CS / Retenção' },
  { key: 'vision', label: 'Reunião Vision' },
  { key: 'reforma_tributaria', label: 'Reforma Tributária' },
];

const ORDENS = {
  recente: { label: 'Reunião mais recente', fn: (a, b) => String(b.ultima).localeCompare(String(a.ultima)) },
  pior: { label: 'Menor nota média', fn: (a, b) => (a.media ?? 99) - (b.media ?? 99) },
  melhor: { label: 'Maior nota média', fn: (a, b) => (b.media ?? -1) - (a.media ?? -1) },
  reunioes: { label: 'Mais reuniões', fn: (a, b) => b.reunioes.length - a.reunioes.length },
  nome: { label: 'Nome do cliente', fn: (a, b) => a.nome.localeCompare(b.nome, 'pt-BR') },
};

function Kpi({ icone, rotulo, valor, classe = 'text-muted', dica }) {
  return (
    <div className="col-6 col-md-3">
      <div className="card border-0 shadow-sm h-100" title={dica}>
        <div className="card-body py-3">
          <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
            <i className={`fas ${icone} me-1 ${classe}`}></i>{rotulo}
          </div>
          <div className="fw-bold" style={{ fontSize: '1.5rem', color: '#212529', fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
        </div>
      </div>
    </div>
  );
}

function Tendencia({ atual, anterior }) {
  if (atual === null || atual === undefined || anterior === null || anterior === undefined) return null;
  const d = atual - anterior;
  if (Math.abs(d) < 0.05) return <i className="fas fa-minus text-muted ms-1" title="Igual à reunião anterior" style={{ fontSize: '0.7rem' }}></i>;
  return d > 0
    ? <i className="fas fa-arrow-up text-success ms-1" title={`+${fmtNota(d)} vs reunião anterior`} style={{ fontSize: '0.75rem' }}></i>
    : <i className="fas fa-arrow-down text-danger ms-1" title={`${fmtNota(d)} vs reunião anterior`} style={{ fontSize: '0.75rem' }}></i>;
}

export default function MemoraAnaliseClientes() {
  const [reunioes, setReunioes] = useState([]);
  const [setoresCad, setSetoresCad] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [setor, setSetor] = useState('');
  const [tipo, setTipo] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState('recente');
  const [abertos, setAbertos] = useState({});
  const [codigoAberto, setCodigoAberto] = useState(null);
  const [clienteSel, setClienteSel] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await axios.get(`${API}/api/reunioes/analise-clientes/`, authHeader());
      setReunioes(r.data?.reunioes || []);
      setSetoresCad(r.data?.setores || []);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não foi possível carregar as reuniões.');
      setReunioes([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // reuniões que passam nos filtros (o setor vale por reunião: ela pode ter vários)
  const filtradas = useMemo(() => {
    const q = norm(busca);
    return reunioes.filter((r) => {
      if (setor && !(r.setores || []).includes(setor)) return false;
      if (tipo && !norm(r.tipo).includes(tipo)) return false;
      if (de && (!r.data || r.data < de)) return false;
      if (ate && (!r.data || r.data > ate)) return false;
      if (q && !norm(`${r.cliente} ${r.assunto} ${r.codigo} ${r.numero_dominio || ''}`).includes(q)) return false;
      return true;
    });
  }, [reunioes, setor, tipo, de, ate, busca]);

  // total de reuniões por setor (sem filtros), para os chips
  const contagemSetor = useMemo(() => {
    const c = {};
    reunioes.forEach((r) => (r.setores || []).forEach((s) => { c[s] = (c[s] || 0) + 1; }));
    return c;
  }, [reunioes]);

  const clientes = useMemo(() => {
    const grupos = {};
    filtradas.forEach((r) => {
      const nome = r.cliente || SEM_CLIENTE;
      const chave = norm(nome);
      if (!grupos[chave]) grupos[chave] = { chave, nome, dominio: null, reunioes: [], setores: new Set() };
      const g = grupos[chave];
      g.reunioes.push(r);
      if (r.numero_dominio && !g.dominio) g.dominio = r.numero_dominio;
      (r.setores || []).forEach((s) => g.setores.add(s));
    });
    return Object.values(grupos).map((g) => {
      const ord = [...g.reunioes].sort((a, b) => String(b.data || '').localeCompare(String(a.data || '')));
      return {
        ...g,
        reunioes: ord,
        setores: [...g.setores],
        media: media(ord.map((r) => r.nota)),
        ultimaNota: ord[0]?.nota ?? null,
        anteriorNota: ord[1]?.nota ?? null,
        ultima: ord[0]?.data || '',
      };
    }).sort(ORDENS[ordem].fn);
  }, [filtradas, ordem]);

  const kpis = useMemo(() => ({
    clientes: clientes.filter((c) => c.nome !== SEM_CLIENTE).length,
    reunioes: filtradas.length,
    media: media(filtradas.map((r) => r.nota)),
    atencao: clientes.filter((c) => c.media !== null && c.media < 6).length,
  }), [clientes, filtradas]);

  const setores = useMemo(() => {
    const lista = [...setoresCad];
    Object.keys(contagemSetor).forEach((s) => { if (!lista.includes(s)) lista.push(s); });
    return lista;
  }, [setoresCad, contagemSetor]);

  const opcoesCliente = useMemo(() => {
    const m = {};
    reunioes.forEach((r) => { if (r.cliente) { const k = norm(r.cliente); if (!m[k]) m[k] = { nome: r.cliente, n: 0 }; m[k].n += 1; } });
    return Object.values(m).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [reunioes]);

  const temFiltro = setor || tipo || de || ate || busca;
  const limpar = () => { setSetor(''); setTipo(''); setDe(''); setAte(''); setBusca(''); };
  const alternar = (chave) => setAbertos((a) => ({ ...a, [chave]: !a[chave] }));
  const fecharReuniao = useCallback(() => setCodigoAberto(null), []);

  return (
    <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center">
          <i className="fas fa-building header-icon me-2"></i>
          <h2 className="titulo-pagina m-0">Análise de Clientes</h2>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={carregar} disabled={carregando}>
          <i className={`fas fa-sync-alt me-1 ${carregando ? 'fa-spin' : ''}`}></i>Atualizar
        </button>
      </div>

      {/* Cliente: escolhido, a página vira o dashboard dele */}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-6 col-lg-5">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
            <i className="fas fa-building me-1"></i>Cliente
          </label>
          <select className="form-select" value={clienteSel} onChange={(e) => setClienteSel(e.target.value)}>
            <option value="">Todos os clientes (visão geral)</option>
            {opcoesCliente.map((c) => <option key={c.nome} value={c.nome}>{c.nome} ({c.n})</option>)}
          </select>
        </div>
      </div>

      {clienteSel ? (
        <MemoraClienteDashboard cliente={clienteSel} onVoltar={() => setClienteSel('')} />
      ) : (<>
      {/* Setor */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-muted me-1" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
          <i className="fas fa-layer-group me-1"></i>Setor:
        </span>
        <button type="button" className={`btn btn-sm ${!setor ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => setSetor('')}>
          Todos <span className="badge bg-light text-dark ms-1">{reunioes.length}</span>
        </button>
        {setores.map((s) => (
          <button key={s} type="button"
            className={`btn btn-sm ${setor === s ? 'btn-dark' : 'btn-outline-secondary'}`}
            onClick={() => setSetor(setor === s ? '' : s)}>
            {s} <span className="badge bg-light text-dark ms-1">{contagemSetor[s] || 0}</span>
          </button>
        ))}
      </div>

      {/* Demais filtros */}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-6 col-md-3">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Tipo de reunião</label>
          <select className="form-select form-select-sm" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            {TIPOS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Reunião de</label>
          <input type="date" className="form-control form-control-sm" value={de} onChange={(e) => setDe(e.target.value)} />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>até</label>
          <input type="date" className="form-control form-control-sm" value={ate} onChange={(e) => setAte(e.target.value)} />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Busca</label>
          <input type="search" className="form-control form-control-sm" placeholder="Cliente, assunto, código, domínio..."
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Ordenar por</label>
          <select className="form-select form-select-sm" value={ordem} onChange={(e) => setOrdem(e.target.value)}>
            {Object.entries(ORDENS).map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
          </select>
        </div>
      </div>
      {temFiltro && (
        <div className="mb-3">
          <button type="button" className="btn btn-link btn-sm px-0 text-decoration-none" onClick={limpar}>
            <i className="fas fa-times me-1"></i>Limpar filtros
          </button>
        </div>
      )}

      {/* Indicadores */}
      <div className="row g-3 mb-3">
        <Kpi icone="fa-building" rotulo="Clientes" valor={kpis.clientes} />
        <Kpi icone="fa-video" rotulo="Reuniões analisadas" valor={kpis.reunioes} />
        <Kpi icone="fa-star" rotulo="Nota média" valor={fmtNota(kpis.media)} classe="text-warning" />
        <Kpi icone="fa-triangle-exclamation" rotulo="Clientes em atenção" valor={kpis.atencao} classe="text-danger"
          dica="Clientes com nota média abaixo de 6 nas reuniões filtradas" />
      </div>

      {erro && <div className="alert alert-danger py-2">{erro}</div>}

      {carregando ? (
        <div className="text-center text-muted py-5">
          <span className="spinner-border spinner-border-sm me-2"></span>Carregando reuniões...
        </div>
      ) : clientes.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
          Nenhuma reunião {temFiltro ? 'com os filtros atuais' : 'analisada com cliente ainda'}.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.88rem' }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Cliente</th>
                <th>Setores</th>
                <th className="text-center">Reuniões</th>
                <th className="text-center">Nota média</th>
                <th className="text-center">Última nota</th>
                <th>Última reunião</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c) => (
                <React.Fragment key={c.chave}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => alternar(c.chave)}>
                    <td className="text-muted"><i className={`fas fa-chevron-${abertos[c.chave] ? 'down' : 'right'}`}></i></td>
                    <td>
                      <div className={`fw-semibold ${c.nome === SEM_CLIENTE ? 'text-muted fst-italic' : ''}`} style={{ color: '#212529' }}>
                        {c.nome}
                        {c.nome !== SEM_CLIENTE && (
                          <button type="button" className="btn btn-link btn-sm py-0 px-1 text-decoration-none" title="Abrir o dashboard do cliente"
                            onClick={(e) => { e.stopPropagation(); setClienteSel(c.reunioes[0]?.cliente || c.nome); }}>
                            <i className="fas fa-chart-pie"></i>
                          </button>
                        )}
                      </div>
                      {c.dominio && <div className="text-muted" style={{ fontSize: '0.75rem' }}>Domínio {c.dominio}</div>}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {c.setores.length ? c.setores.map((s) => (
                          <span key={s} className="badge rounded-pill bg-light text-dark border">{s}</span>
                        )) : <span className="text-muted">-</span>}
                      </div>
                    </td>
                    <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.reunioes.length}</td>
                    <td className="text-center"><span className={`badge ${badgeNota(c.media)}`} style={{ fontSize: '0.8rem' }}>{fmtNota(c.media)}</span></td>
                    <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtNota(c.ultimaNota)}<Tendencia atual={c.ultimaNota} anterior={c.anteriorNota} />
                    </td>
                    <td>{fmtData(c.ultima)}</td>
                  </tr>
                  {abertos[c.chave] && (
                    <tr>
                      <td></td>
                      <td colSpan={6} className="bg-light">
                        <table className="table table-sm table-hover mb-0 bg-white" style={{ fontSize: '0.83rem' }}>
                          <thead>
                            <tr className="text-muted">
                              <th>Data</th><th>Reunião</th><th>Tipo</th><th>Setor</th>
                              <th className="text-center">Nota</th><th>Operadores</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.reunioes.map((r) => (
                              <tr key={r.codigo} style={{ cursor: 'pointer' }} onClick={() => setCodigoAberto(r.codigo)}>
                                <td className="text-nowrap">{fmtData(r.data)}{r.hora ? <span className="text-muted"> {r.hora}</span> : null}</td>
                                <td>
                                  <div style={{ color: '#212529' }}>{r.assunto || '-'}</div>
                                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>{r.codigo}{r.analisado_por ? ` · analisada por ${r.analisado_por}` : ''}</div>
                                </td>
                                <td className="text-nowrap">{labelTipoReuniao(r.tipo)}</td>
                                <td>{(r.setores || []).join(', ') || '-'}</td>
                                <td className="text-center"><span className={`badge ${badgeNota(r.nota)}`}>{fmtNota(r.nota)}</span></td>
                                <td>
                                  {(r.operadores || []).length ? r.operadores.map((o, i) => (
                                    <span key={i} className="me-2 text-nowrap">{o.nome} <span className="text-muted">({fmtNota(o.nota)})</span></span>
                                  )) : <span className="text-muted">-</span>}
                                </td>
                                <td className="text-end text-muted"><i className="fas fa-up-right-from-square" title="Ver análise completa"></i></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>)}

      {codigoAberto && <ReuniaoAnaliseModal codigo={codigoAberto} onClose={fecharReuniao} />}
    </div>
  );
}
